const SINGLE_SETTINGS = [
	"slackConnectSettings",
	"intercomSettings",
	"hubspotSettings",
	"plainSettings",
	"teamsSettings",
	"githubSettings",
	"aiChatSettings",
	"ssoSettings",
] as const

type SingleKey = (typeof SINGLE_SETTINGS)[number]

interface Stamped {
	updatedAt?: number | null
	createdAt?: number | null
}

type WithSingleSettings<W> = Omit<W, SingleKey> & {
	[K in SingleKey & keyof W]: W[K] extends readonly (infer Row)[]
		? Row | undefined
		: W[K]
}

function newest<Row>(rows: readonly Row[]): Row | undefined {
	let best: Row | undefined
	let bestAt = -Infinity
	for (const row of rows) {
		const { updatedAt, createdAt } = row as Stamped
		const at = updatedAt ?? createdAt ?? 0
		if (best === undefined || at > bestAt) {
			best = row
			bestAt = at
		}
	}
	return best
}

// Each of these is one row per workspace in Postgres, but a reconnect can leave
// the old and new row side by side in the synced copy for a moment; Zero's
// `.one()` throws on that, so the query reads lists and this keeps the newest.
export function withSingleSettings<W extends object>(
	workspace: W,
): WithSingleSettings<W> {
	const result = { ...workspace } as Record<string, unknown>
	for (const key of SINGLE_SETTINGS) {
		const value = result[key]
		if (Array.isArray(value)) result[key] = newest(value)
	}
	return result as WithSingleSettings<W>
}
